import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Copy, RefreshCw, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const driverLink = (token) => `${window.location.origin}/chauffeur/${token}`;

export const toWhatsAppNumber = (phone) => {
  let n = (phone || '').replace(/[^\d+]/g, '');
  if (n.startsWith('+')) return n.slice(1);
  if (n.startsWith('00')) return n.slice(2);
  if (n.startsWith('0')) return `31${n.slice(1)}`;
  return n;
};

export const shareDriverViaWhatsApp = async (name, token, date = '') => {
  const win = window.open('', '_blank');
  try {
    const { data } = await axios.get(`${API}/admin/chauffeur-drivers/${name}/share`, {
      params: { date }, headers: { Authorization: `Bearer ${token}` },
    });
    if (!data.phone) {
      if (win) win.close();
      toast.error(`Vul eerst het WhatsApp-nummer van ${name} in bij "Chauffeurs instellen"`);
      return;
    }
    const link = driverLink(data.token);
    const text = [
      `Hoi ${name}! Je ophaalroute ${data.is_today ? 'van vandaag' : `voor ${new Date(`${data.date}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}`}: ${data.total_motors} motoren bij ${data.stop_count} leveranciers.`,
      ...(data.is_today ? [] : ['(Deze route verschijnt op die dag in je afvinklijst.)']),
      '', '\u{1F4F1} Afvinken per stop:', link,
      '', '\u{1F5FA}\uFE0F Route in Google Maps:', data.google_maps_url || '(nog geen stops)',
      '', '\u{1F4C4} Route-PDF:', `${link}?pdf=1`,
      '', 'Je pincode heb je van ons gekregen.',
    ].join('\n');
    const url = `https://wa.me/${toWhatsAppNumber(data.phone)}?text=${encodeURIComponent(text)}`;
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (win) win.close();
    toast.error(e?.response?.data?.detail || 'WhatsApp-bericht maken mislukt');
  }
};

export const openDriverPdf = async (drv, token, date = '') => {
  const win = window.open('', '_blank');
  try {
    const res = await axios.get(`${API}/admin/chauffeur-route-pdf`, {
      params: { driver: drv, date }, headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (win) win.close();
    toast.error(`PDF maken voor ${drv} mislukt`);
  }
};

const DriverRow = ({ driver, token, onSaved }) => {
  const [phone, setPhone] = useState(driver.phone || '');
  const [pin, setPin] = useState(driver.pin || '');
  const headers = { Authorization: `Bearer ${token}` };

  const save = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/chauffeur-drivers/${driver.name}`, { phone, pin }, { headers });
      onSaved(data);
      toast.success(`${driver.name} opgeslagen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    }
  };
  const regenerate = async () => {
    if (!window.confirm(`Nieuwe link voor ${driver.name}? De oude link werkt dan niet meer.`)) return;
    const { data } = await axios.post(`${API}/admin/chauffeur-drivers/${driver.name}/regenerate`, {}, { headers });
    onSaved(data);
    toast.success('Nieuwe link gemaakt — stuur hem opnieuw via WhatsApp');
  };
  const copy = async () => {
    await navigator.clipboard.writeText(driverLink(driver.token));
    toast.success('Link gekopieerd');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_90px_auto] gap-2 items-center py-2 border-b border-zinc-100 last:border-0" data-testid={`driver-settings-${driver.name}`}>
      <div className="font-semibold text-zinc-900 text-sm">{driver.name}</div>
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+41 79 123 45 67"
             className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm" data-testid={`driver-phone-${driver.name}`} />
      <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="PIN"
             className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm font-mono tracking-widest" data-testid={`driver-pin-${driver.name}`} />
      <div className="flex gap-1">
        <button onClick={save} className="px-2.5 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium flex items-center gap-1" data-testid={`driver-save-${driver.name}`}>
          <Save className="w-3.5 h-3.5" /> Opslaan
        </button>
        <button onClick={copy} title="Link kopiëren" className="px-2 py-1.5 rounded-lg border border-zinc-300 text-zinc-700" data-testid={`driver-copy-link-${driver.name}`}>
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button onClick={regenerate} title="Nieuwe link maken" className="px-2 py-1.5 rounded-lg border border-zinc-300 text-zinc-700" data-testid={`driver-regenerate-${driver.name}`}>
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export const DriverSettingsPanel = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/chauffeur-drivers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDrivers(r.data))
      .catch(() => toast.error('Chauffeurs laden mislukt'));
  }, [open, token]);

  const onSaved = (d) => setDrivers((ds) => ds.map((x) => (x.name === d.name ? d : x)));

  return (
    <div className="bg-white rounded-xl border border-zinc-200">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-4 text-sm font-semibold text-zinc-900" data-testid="driver-settings-toggle">
        <Settings className="w-4 h-4" /> Chauffeurs instellen (WhatsApp-nummer, pincode, link)
        <span className="ml-auto text-zinc-400">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-zinc-500 mb-2">
            Gebruik internationaal formaat (+31…, +41…). Geef de pincode mondeling door — die staat niet in het WhatsApp-bericht.
          </p>
          {drivers.map((d) => <DriverRow key={`${d.name}-${d.token}`} driver={d} token={token} onSaved={onSaved} />)}
        </div>
      )}
    </div>
  );
};
